import { DownloadOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Card,
	Col,
	DatePicker,
	Form,
	Row,
	Segmented,
	Space,
	Statistic,
	Table,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type GroupBy = 'day' | 'week' | 'month';

interface FinancialRow {
	period: string;
	collected: number;
	paid_out: number;
	net: number;
	running_total: number;
}

interface FinancialTotals {
	collected: number;
	paid_out: number;
	net: number;
	running_total: number;
}

interface FinancialReportResponse {
	rows: FinancialRow[];
	totals: FinancialTotals;
	count: number;
	group_by: GroupBy;
}

interface FilterValues {
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const ugx = (v: number) => `UGX ${formatNumber(v)}`;

const quickPresets: { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[] = [
	{ label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
	{ label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
	{ label: 'This Year', value: [dayjs().startOf('year'), dayjs()] },
];

const periodLabel: Record<GroupBy, string> = {
	day: 'Date',
	week: 'Week of',
	month: 'Month',
};

const formatPeriod = (period: string, groupBy: GroupBy) => {
	const d = dayjs(period);
	if (groupBy === 'month') return d.format('MMM YYYY');
	if (groupBy === 'week') return `${d.format('DD MMM YYYY')}`;
	return d.format('DD MMM YYYY');
};

const FinancialReport = () => {
	const [form] = Form.useForm<FilterValues>();
	const [groupBy, setGroupBy] = useState<GroupBy>('day');
	const [filters, setFilters] = useState<FilterValues>({
		date_range: [dayjs().startOf('month'), dayjs()],
	});

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['financial-report', filters, groupBy],
		queryFn: async () => {
			const params = {
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
				group_by: groupBy,
			};
			const response = await axios.get('/admin/reports/financial', { withCredentials: true, params });
			return response.data.results as FinancialReportResponse;
		},
	});

	if (error) handleAPIError(error);

	const rows: FinancialRow[] = data?.rows || [];
	const totals = data?.totals;

	const handleFilter = () => {
		setFilters(form.getFieldsValue());
	};

	const handleReset = () => {
		const defaultRange: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs().startOf('month'), dayjs()];
		form.setFieldsValue({ date_range: defaultRange });
		setFilters({ date_range: defaultRange });
	};

	const handleExport = () => {
		if (!rows.length) return;
		const sheetRows = rows.map((r) => ({
			[periodLabel[groupBy]]: formatPeriod(r.period, groupBy),
			'Collected (UGX)': r.collected,
			'Paid Out (UGX)': r.paid_out,
			'Net (UGX)': r.net,
			'Running Total (UGX)': r.running_total,
		}));
		if (totals) {
			sheetRows.push({
				[periodLabel[groupBy]]: 'TOTALS',
				'Collected (UGX)': totals.collected,
				'Paid Out (UGX)': totals.paid_out,
				'Net (UGX)': totals.net,
				'Running Total (UGX)': totals.running_total,
			});
		}
		const sheet = XLSX.utils.json_to_sheet(sheetRows);
		sheet['!cols'] = [{ wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 22 }];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, sheet, 'Financial');
		const start = filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : 'all';
		const end = filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : 'now';
		XLSX.writeFile(wb, `financial-report-${start} to ${end}.xlsx`);
	};

	const columns: ColumnsType<FinancialRow> = [
		{
			title: periodLabel[groupBy],
			dataIndex: 'period',
			key: 'period',
			width: 140,
			render: (v: string) => formatPeriod(v, groupBy),
		},
		{
			title: 'Collected',
			dataIndex: 'collected',
			key: 'collected',
			width: 180,
			align: 'right',
			render: (v: number) => <Text style={{ color: '#52c41a' }}>{ugx(v)}</Text>,
			sorter: (a, b) => a.collected - b.collected,
		},
		{
			title: 'Paid Out',
			dataIndex: 'paid_out',
			key: 'paid_out',
			width: 180,
			align: 'right',
			render: (v: number) => <Text style={{ color: '#fa8c16' }}>{ugx(v)}</Text>,
			sorter: (a, b) => a.paid_out - b.paid_out,
		},
		{
			title: 'Net',
			dataIndex: 'net',
			key: 'net',
			width: 180,
			align: 'right',
			render: (v: number) => (
				<Text strong style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>
					{ugx(v)}
				</Text>
			),
			sorter: (a, b) => a.net - b.net,
		},
		{
			title: (
				<Tooltip title="Cumulative net cash flow from the start of the selected period">
					Running Total
				</Tooltip>
			),
			dataIndex: 'running_total',
			key: 'running_total',
			width: 200,
			align: 'right',
			render: (v: number) => <Text style={{ color: '#1677ff' }}>{ugx(v)}</Text>,
		},
	];

	return (
		<AppLayout title="Financial Report" subtitle="Cash flow by collection and payout transactions">
			{/* Summary Cards */}
			{totals && (
				<Card className="mb-6">
					<Row gutter={[16, 16]}>
						<Col xs={12} sm={6}>
							<Statistic
								title="Total Collected"
								value={totals.collected}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: '#52c41a' }}
							/>
						</Col>
						<Col xs={12} sm={6}>
							<Statistic
								title="Total Paid Out"
								value={totals.paid_out}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: '#fa8c16' }}
							/>
						</Col>
						<Col xs={12} sm={6}>
							<Statistic
								title="Net Cash Flow"
								value={totals.net}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: totals.net >= 0 ? '#52c41a' : '#ff4d4f' }}
							/>
						</Col>
						<Col xs={12} sm={6}>
							<Tooltip title="Cumulative net at end of selected period">
								<Statistic
									title="Period End Balance"
									value={totals.running_total}
									formatter={(v) => ugx(v as number)}
									valueStyle={{ color: '#1677ff' }}
								/>
							</Tooltip>
						</Col>
					</Row>
				</Card>
			)}

			{/* Filters */}
			<Card className="mb-4">
				<div className="flex flex-wrap items-center gap-3">
					<Form
						form={form}
						layout="inline"
						initialValues={{ date_range: [dayjs().startOf('month'), dayjs()] }}
					>
						<Form.Item name="date_range" label="Date Range">
							<RangePicker style={{ width: 280 }} placeholder={['Start Date', 'End Date']} />
						</Form.Item>
					</Form>
					<Space wrap>
						{quickPresets.map((p) => (
							<Button
								key={p.label}
								size="small"
								onClick={() => {
									form.setFieldsValue({ date_range: p.value });
									setFilters({ date_range: p.value });
								}}
							>
								{p.label}
							</Button>
						))}
					</Space>
					<Segmented
						value={groupBy}
						onChange={(v) => setGroupBy(v as GroupBy)}
						options={[
							{ label: 'Day', value: 'day' },
							{ label: 'Week', value: 'week' },
							{ label: 'Month', value: 'month' },
						]}
					/>
					<Space>
						<Button type="primary" icon={<FilterOutlined />} onClick={handleFilter}>
							Apply
						</Button>
						<Button icon={<ReloadOutlined />} onClick={handleReset}>
							Reset
						</Button>
						<Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!rows.length}>
							Export
						</Button>
						<Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
							Refresh
						</Button>
					</Space>
				</div>
			</Card>

			{/* Table */}
			<Card>
				<Table
					columns={columns}
					dataSource={rows}
					loading={isLoading}
					rowKey="period"
					scroll={{ x: 900 }}
					pagination={false}
					summary={() =>
						totals ? (
							<Table.Summary fixed>
								<Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
									<Table.Summary.Cell index={0}>TOTALS</Table.Summary.Cell>
									<Table.Summary.Cell index={1} align="right">
										<Text style={{ color: '#52c41a' }}>{ugx(totals.collected)}</Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={2} align="right">
										<Text style={{ color: '#fa8c16' }}>{ugx(totals.paid_out)}</Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={3} align="right">
										<Text strong style={{ color: totals.net >= 0 ? '#52c41a' : '#ff4d4f' }}>
											{ugx(totals.net)}
										</Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={4} align="right">
										<Text style={{ color: '#1677ff' }}>{ugx(totals.running_total)}</Text>
									</Table.Summary.Cell>
								</Table.Summary.Row>
							</Table.Summary>
						) : undefined
					}
				/>
			</Card>
		</AppLayout>
	);
};

export default FinancialReport;
